/**
 * AdminPartnerServiceAreasPage — /admin/partner-service-areas (Phase J).
 *
 * Read-only observability queue for support staff. Columns: partner
 * user id, primary_zip, additional zip count, tier, cap, over-cap flag,
 * last-updated. Filters: over-cap, missing-primary, by tier, by zip.
 *
 * Admin cannot edit — outreach happens off-platform per Phase J spec.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Loader2, RefreshCw, AlertTriangle } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TIER_OPTIONS = ["all", "Standard", "Preferred", "Elite"];

/* ------------------------------------------------------------------ */
export default function AdminPartnerServiceAreasPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("all");
  const [overCapFilter, setOverCapFilter] = useState("all");
  const [zipFilter, setZipFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 200 };
      if (tierFilter !== "all") params.tier_name = tierFilter;
      if (overCapFilter === "over") params.over_cap = "true";
      if (overCapFilter === "under") params.over_cap = "false";
      if (overCapFilter === "missing_primary") params.missing_primary = "true";
      if (zipFilter.trim()) params.zip = zipFilter.trim();
      const { data } = await api.get("/admin/partner-service-areas", {
        params,
      });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tierFilter, overCapFilter, zipFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      style={{ padding: "24px" }}
      data-testid="admin-partner-service-areas-page"
    >
      <header style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MapPin size={22} color="#F89623" />
          <h1
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#374751",
              margin: 0,
            }}
            data-testid="admin-partner-service-areas-heading"
          >
            Partner Service Areas
          </h1>
        </div>
        <p style={{ color: "#4b5563", marginTop: 6 }}>
          Read-only. Admin outreach for over-cap partners happens
          off-platform.
        </p>
      </header>

      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 20,
          alignItems: "center",
        }}
      >
        <div style={{ minWidth: 200 }}>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger data-testid="admin-partner-service-areas-tier-filter">
              <SelectValue placeholder="Filter by tier" />
            </SelectTrigger>
            <SelectContent>
              {TIER_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>
                  {t === "all" ? "All tiers" : t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div style={{ minWidth: 220 }}>
          <Select value={overCapFilter} onValueChange={setOverCapFilter}>
            <SelectTrigger data-testid="admin-partner-service-areas-cap-filter">
              <SelectValue placeholder="Cap status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All partners</SelectItem>
              <SelectItem value="over">Over cap only</SelectItem>
              <SelectItem value="under">Under cap only</SelectItem>
              <SelectItem value="missing_primary">Missing primary ZIP</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Input
          value={zipFilter}
          onChange={(e) => setZipFilter(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="Search by ZIP"
          maxLength={5}
          style={{ maxWidth: 200 }}
          data-testid="admin-partner-service-areas-zip-filter"
        />

        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-partner-service-areas-refresh"
        >
          {loading ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <RefreshCw size={16} style={{ marginRight: 6 }} /> Reload
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <div data-testid="admin-partner-service-areas-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="admin-partner-service-areas-empty"
          style={{
            padding: 24,
            background: "#f9fafb",
            borderRadius: 12,
            color: "#6b7280",
          }}
        >
          No partners in this bucket.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
            data-testid="admin-partner-service-areas-table"
          >
            <thead>
              <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                <th style={cellHead}>Partner user id</th>
                <th style={cellHead}>Primary</th>
                <th style={cellHead}>Additional (count)</th>
                <th style={cellHead}>Tier</th>
                <th style={cellHead}>Cap</th>
                <th style={cellHead}>Over cap?</th>
                <th style={cellHead}>Last updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  style={{ borderTop: "1px solid #e5e7eb" }}
                  data-testid={`admin-partner-service-areas-row-${r.id}`}
                >
                  <td style={cell}>
                    <code style={{ fontSize: 12 }}>{r.partner_user_id}</code>
                  </td>
                  <td style={cell}>
                    {r.primary_zip ? (
                      <code>{r.primary_zip}</code>
                    ) : (
                      <span style={{ color: "#991b1b", fontWeight: 600 }}>
                        (none)
                      </span>
                    )}
                  </td>
                  <td style={cell}>{(r.additional_zips || []).length}</td>
                  <td style={cell}>{r.tier_name || "—"}</td>
                  <td style={cell}>{r.tier_cap ?? "—"}</td>
                  <td style={cell}>
                    {r.over_cap ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          color: "#991b1b",
                          fontWeight: 700,
                        }}
                        data-testid={`admin-partner-service-areas-over-cap-${r.id}`}
                      >
                        <AlertTriangle size={14} /> Over
                      </span>
                    ) : (
                      <span style={{ color: "#6b7280" }}>—</span>
                    )}
                  </td>
                  <td style={cell}>{r.updated_at?.slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const cellHead = {
  padding: "10px 14px",
  fontWeight: 700,
  color: "#374751",
  fontSize: 13,
};
const cell = { padding: "10px 14px", color: "#374751" };
